import React, { forwardRef, useEffect, useRef, useState } from 'react';


import 'react-datepicker/dist/react-datepicker.css';

import './style.css';
import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import DatePicker from 'react-datepicker';
import { Input } from 'components/Input';
import { postUser } from 'api/user';
import { useAuth } from 'contexts/AuthContext';
import useDebounce from 'hooks/useDebounce';
import useEmailChecker from 'hooks/useCheckEmail';
import { UserPost } from 'types/user';
import useUsernameChecker from 'hooks/useCheckUsername';
import {
  EMAIL_REG,
  filterRegEx,
  USERNAME_REG,
  validateRegEx,
  ZIPCODE_REG,
} from 'utils/regex';
import { useMutation, useQueryClient } from '@tanstack/react-query';


// Using any because 3rd party package props
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomInput = forwardRef((props: any, ref) => {
  return <Input accessibilityLabel='birthday input' icon="birthday" label="Birthday" {...props} ref={ref} />;
});
CustomInput.displayName = 'customInput';

export const CreateUser = () => {
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [invalidEmail, setInvalidEmail] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [invalidUsername, setInvalidUsername] = useState<string>('');
  const [zipcode, setZipcode] = useState<string>('');
  const [invalidZipcode, setInvalidZipcode] = useState<string>('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const inputRef = useRef(null);
  const sixteenYearsAgo = new Date();
  sixteenYearsAgo.setFullYear(sixteenYearsAgo.getFullYear() - 16);
  const [birthday, setBirthday] = useState(sixteenYearsAgo);
  const { firebaseUser, setLoading, loading, logout } = useAuth();
  const queryClient = useQueryClient();

  const DEBOUNCE_DELAY = 500;
  const debouncedEmail = useDebounce(email, DEBOUNCE_DELAY);
  const debouncedUsername = useDebounce(username, DEBOUNCE_DELAY);

  const { checkUsername, checkUsernameLoading, checkUsernameError } =
    useUsernameChecker(debouncedUsername);

  const { checkEmail, checkEmailLoading, checkEmailError } =
    useEmailChecker(debouncedEmail);

  useEffect(() => {
    const validEmail = validateRegEx(email, EMAIL_REG);
    if (validEmail && !checkEmail) {
      setInvalidEmail('');
    } else if (validEmail && checkEmail) {
      setInvalidEmail('Email already taken.');
    } else {
      setInvalidEmail('Email invalid.');
    }
  }, [debouncedEmail, checkEmail]);

  useEffect(() => {
    const validUsername = validateRegEx(username, USERNAME_REG);
    if (validUsername && !checkUsername) {
      setInvalidUsername('');
    } else if (validUsername && checkUsername) {
      setInvalidUsername('Username already taken.');
    } else {
      setInvalidUsername(
        'Invalid username: Please use alphanumeric characters only.'
      );
    }
  }, [debouncedUsername, checkUsername]);

  useEffect(() => {
    const newZipCode = filterRegEx(zipcode, ZIPCODE_REG);
    if (newZipCode !== zipcode) {
      setZipcode(newZipCode);
    }

    if (newZipCode.length && newZipCode.length < 5) {
      setInvalidZipcode(
        'Invalid zip code: Please enter a valid zip code'
      );
    } else {
      setInvalidZipcode('');
    }
  }, [zipcode]);

  const checkButtonEnabled = () => {
    return !!(
      email &&
      invalidEmail == '' &&
      username &&
      invalidUsername == '' &&
      firstName &&
      lastName &&
      zipcode &&
      zipcode.length === 5 &&
      birthday &&
      !checkUsernameError &&
      !checkEmailError &&
      !checkEmailLoading &&
      !checkUsernameLoading &&
      agreedToTerms
    );
  };

  const userMutation = useMutation((newUser: UserPost) => postUser(newUser), {
    retry: 2,
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: (data, user) => {
      queryClient.setQueryData(['user', user.userId], user);
      queryClient.invalidateQueries(['user', user.userId]);
      setLoading(false);
    },
    onError: (error, user) => {
      setLoading(false);
      console.error('Error on create user mutation: ', error);
      queryClient.invalidateQueries(['user', user.userId]);
    },
  });

  const createAccount = () => {
    const newUser: UserPost = {
      firstName,
      lastName,
      username,
      email,
      userId: firebaseUser?.uid || '',
      firebaseId: firebaseUser?.uid || '',
      phoneNumber: firebaseUser?.phoneNumber || '',
      zipCode: zipcode,
      dob: birthday.toISOString(),
    };
    userMutation.mutate(newUser);
  };

  return (
    <div className='create-user-container'>
      <h2 className='create-user-text'>Register User</h2>
      <form id="create-user-form">
        <Input 
          id='first-name-input'
          accessibilityLabel='first name input'
          value={firstName}
          handleOnChange={setFirstName}
          label='First Name'
        />
        <Input 
          id='last-name-input'
          accessibilityLabel='last name input'
          value={lastName}
          handleOnChange={setLastName}
          label='Last Name'
        />
        <Input 
          id='email-input'
          accessibilityLabel='email input'
          value={email}
          handleOnChange={setEmail}
          icon='email'
          label='Email'
          error={invalidEmail}
        />
        <Input 
          id='username-input'
          accessibilityLabel='username input'
          value={username}
          handleOnChange={setUsername}
          icon='username'
          label='Username'
          error={invalidUsername}
        />
        <Input 
          id='zipcode-input'
          accessibilityLabel='zipcode input'
          value={zipcode}
          handleOnChange={setZipcode}
          icon='zip'
          label='Zipcode'
          error={invalidZipcode}
        />
        <DatePicker
          customInput={<CustomInput inputRef={inputRef} />}
          selected={birthday}
          onChange={(date:Date) => setBirthday(date)} 
          peekNextMonth
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          showDisabledMonthNavigation
          maxDate={sixteenYearsAgo}
        />
        <p className='age-limit-text'>
          You must be sixteen years or older to join VLTED.
        </p>
        <div className='checkbox-and-link-container'>
          <Checkbox
            accessibilityLabel='terms of service checkbox'
            label='I agree to the '
            value={agreedToTerms}
            onChange={() => setAgreedToTerms(!agreedToTerms)}/>
          <a
            className='terms-link'
            href={'https://www.vlted.com/terms-of-service'}
            target="_blank"
            rel="noopener noreferrer">{'Terms of Service'}
          </a>
        </div>
      </form>
      <Button
        accessibilityLabel='create user button'
        type={'primary'}
        text={'Create User'}
        disabled={!checkButtonEnabled()}
        handleOnClick={() => createAccount()}
        loading={userMutation.isLoading} />
      <Button
        accessibilityLabel='back button'
        type={'secondary'}
        text={'Back'}
        handleOnClick={() => logout()}
        loading={loading} />
    </div>
  );
};
